import { royalMomentumInitialState } from "../features/royalMomentum/royalMomentumSlice";

export const STATE_KEY = "royalMomentumState";

// The WebView container updates the bundled HTML/JS while localStorage stays,
// so this blob can have been written by ANY previous bundle version. Restoring
// it verbatim as preloadedState replaced the slice's initialState wholesale —
// a blob predating a shape change left branches undefined, the first render
// that dereferenced one crashed, and (pre-error-boundary bundles) the app
// whitescreened at boot faster than the container's updater could run: a
// permanently bricked install. Commit 87c39bde6 was this exact failure, fixed
// at one call site. loadState therefore never trusts the stored shape: it
// picks only the durable whitelisted fields and merges them over the slice
// defaults, so any legacy or poisoned blob degrades to defaults instead of
// crashing the boot.
const STATE_VERSION = 1;

// RULES FOR CHANGING PERSISTED STATE:
// 1. To persist a new field: add it here AND give it a default in the slice's
//    initialState. Old blobs then fall back to the default automatically.
// 2. To change an existing persisted field's shape incompatibly: RENAME the
//    field (e.g. visitFormData -> visitFormDataV2) and update this list — the
//    old key is then ignored automatically. Only write custom migration code
//    (like the step5/step6 merge below) if the old data must be preserved.
//
// Transient fields (data, loading, error) are deliberately absent: a blob
// captured with loading:true rebooted the app into a stuck loader.
const PERSISTED_FIELDS = [
    "selectedCountry",
    "selectedBrand",
    "currentVisitId",
    "visitProgress",
    "visitFormData",
];

const isPlainObject = (value) =>
    typeof value === "object" && value !== null && !Array.isArray(value);

export const loadState = () => {
    try {
        const raw = localStorage.getItem(STATE_KEY);
        if (raw === null) return undefined;
        const parsed = JSON.parse(raw);
        // New blobs are { __v, state }; legacy blobs are the flat slice object.
        // Both are migrated through the same whitelist+merge, so existing
        // healthy installs keep their country/brand selection.
        const source = isPlainObject(parsed)
            ? isPlainObject(parsed.state)
                ? parsed.state
                : parsed
            : {};

        const merged = { ...royalMomentumInitialState };
        for (const key of PERSISTED_FIELDS) {
            const stored = source[key];
            if (stored === undefined) continue;
            merged[key] =
                isPlainObject(royalMomentumInitialState[key]) && isPlainObject(stored)
                    ? { ...royalMomentumInitialState[key], ...stored }
                    : stored;
        }

        // One level deeper for the visit form: each step's defaults must exist
        // even when the stored blob predates fields inside a step.
        const initForm = royalMomentumInitialState.visitFormData;
        const storedForm = isPlainObject(merged.visitFormData) ? merged.visitFormData : {};
        merged.visitFormData = {
            ...initForm,
            ...storedForm,
            step5: { ...initForm.step5, ...(isPlainObject(storedForm.step5) ? storedForm.step5 : {}) },
            step6: { ...initForm.step6, ...(isPlainObject(storedForm.step6) ? storedForm.step6 : {}) },
        };

        return { royalMomentum: merged };
    } catch (err) {
        return undefined;
    }
};

export const saveState = (state) => {
    try {
        const slice = state.royalMomentum ?? {};
        const durable = {};
        for (const key of PERSISTED_FIELDS) {
            durable[key] = slice[key];
        }
        localStorage.setItem(STATE_KEY, JSON.stringify({ __v: STATE_VERSION, state: durable }));
    } catch (err) {
        // Ignore write errors (quota, storage disabled) — persistence is best-effort.
    }
};
